<script setup lang="ts">
import type { AlternativesEntry } from '~/composables/useAlternativesEntry'
import { useAlternativesButton } from '~/composables/useAlternativesButton'
import IconBellOff from '~icons/mdi/bell-off-outline'
import IconBell from '~icons/mdi/bell-outline'
import IconAlternative from '~icons/mdi/shopping-search-outline'

interface Props {
  alternatives?: AlternativesEntry | null
  reminder: boolean | undefined
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:reminder', value: boolean): void
  (e: 'explore:alternatives'): void
}>()

const { isVisible: showAlternativesBtn, navTo: alternativesNavTo, handleClick: handleAlternativesClick } = useAlternativesButton(
  () => props.alternatives,
  () => emit('explore:alternatives'),
)

const subscribeComputed = computed({
  get: () => props.reminder,
  set: (value: boolean) => {
    emit('update:reminder', value)
  },
})
const reminderHandler = () => subscribeComputed.value = !subscribeComputed.value
</script>

<template>
  <!--
   We search for alternatives by generating the path,
   so we are able to track where the customer added the product from.
   Therefore, the URL is being saved as a source at the cart.
  -->
  <div class="d-flex flex-wrap justify-end ga-2 w-100">
    <v-btn
      v-if="showAlternativesBtn"
      data-nav-id="product_explore_alternatives"
      variant="outlined"
      class="alternative-btn flex-shrink-0"
      data-test-id="product-alternativeBtn"
      :to="alternativesNavTo"
      @click="handleAlternativesClick"
    >
      {{ $t('product.alternative.button') }}
      <template #prepend>
        <v-icon size="large" class="">
          <IconAlternative />
        </v-icon>
      </template>
    </v-btn>
    <!--  Todo: add shop::display-subscription-button feature flag -->
    <v-btn
      :prepend-icon="subscribeComputed ? IconBellOff : IconBell"
      color="secondary"
      height="40"
      class="flex-shrink-0"
      data-test-id="product-reminderBtn"
      data-track-id="product_toggle_reminder"
      @click="reminderHandler"
    >
      {{ subscribeComputed ? $t('product.subscription.button.isSubscribed') : $t('product.subscription.button.subscribe') }}
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
.alternative-btn {
  height: auto;
  min-height: 40px;

  :deep(.v-btn__overlay) {
    opacity: 0 !important;
  }

  :deep(.v-btn__content) {
    white-space: normal;
  }
}
</style>
